/* ==========================================================================
   CUSTOM FANS THEME — Vanilla JS interactions
   - Scroll reveal via IntersectionObserver
   - Custom cursor (hero opt-in)
   - Utilities exposed on window.CFT for per-section scripts
   ========================================================================== */
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    const scope = root || document;
    const targets = scope.querySelectorAll('.cft-reveal:not(.is-in)');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* ---------- Custom cursor ---------- */
  function initCustomCursor() {
    if (prefersReducedMotion) return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    const host = document.querySelector('[data-cft-cursor]');
    if (!host) return;

    const cursor = document.createElement('div');
    cursor.className = 'cft-cursor';
    document.body.appendChild(cursor);

    let x = 0, y = 0, tx = 0, ty = 0, raf;
    const render = () => {
      x += (tx - x) * 0.18;
      y += (ty - y) * 0.18;
      cursor.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(render);
    };

    host.addEventListener('mouseenter', () => cursor.classList.add('is-active'));
    host.addEventListener('mouseleave', () => cursor.classList.remove('is-active', 'is-hover'));
    host.addEventListener('mousemove', (e) => { tx = e.clientX; ty = e.clientY; });
    host.querySelectorAll('a, button').forEach((el) => {
      el.addEventListener('mouseenter', () => cursor.classList.add('is-hover'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('is-hover'));
    });
    raf = requestAnimationFrame(render);
  }

  /* ---------- Lightweight event bus (used by sections) ---------- */
  const bus = new EventTarget();

  /* ---------- Public API ---------- */
  window.CFT = {
    initReveal,
    bus,
    on: (evt, cb) => bus.addEventListener(evt, cb),
    emit: (evt, detail) => bus.dispatchEvent(new CustomEvent(evt, { detail })),
    reducedMotion: prefersReducedMotion,
  };

  /* ---------- Product gallery (delegated, morph-nav proof) ----------
     The product section's inline <script> does not re-run on Horizon's
     instant/morph navigation, so the gallery would only work on a hard
     reload. We bind on `document` instead — those listeners survive
     navigation — and drive the gallery purely from the DOM. Adds touch
     swipe on the main image, which the inline version never had. */
  function galleryFor(el) {
    // el is any node inside a .cft-pp gallery; return its controls.
    const root = el.closest('.cft-pp');
    if (!root) return null;
    const main = root.querySelector('.cft-pp__main-image');
    const thumbs = Array.prototype.slice.call(root.querySelectorAll('.cft-pp__thumb'));
    if (!main || thumbs.length === 0) return null;
    return { root, main, thumbs };
  }

   function showThumb(ctx, idx) {
    if (idx < 0) idx = ctx.thumbs.length - 1;
    if (idx >= ctx.thumbs.length) idx = 0;
    const thumb = ctx.thumbs[idx];

    // Source grande taille
    let src = thumb.getAttribute('data-cft-thumb-src');
    if (!src) {
      const inner = thumb.querySelector('img');
      if (inner) src = inner.currentSrc || inner.src;
    }

    // Feedback immédiat sur les vignettes (toujours, même si l'image n'a pas encore swappé)
    ctx.thumbs.forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    ctx.root.dataset.cftActiveIdx = String(idx);

    if (!src || !ctx.main) return;
    if (ctx.main.getAttribute('src') === src) return;

    // Précharge l'image AVANT le crossfade — c'est ce qui élimine le flash blanc
    const preloader = new Image();
    preloader.onload = () => {
      ctx.main.style.transition = 'opacity 220ms cubic-bezier(0.22, 1, 0.36, 1)';
      ctx.main.style.opacity = '0';
      setTimeout(() => {
        ctx.main.removeAttribute('srcset');
        ctx.main.removeAttribute('sizes');
        ctx.main.setAttribute('src', src);
        void ctx.main.offsetWidth; // force reflow
        ctx.main.style.opacity = '1';
      }, 200);
    };
    preloader.onerror = () => {
      // Fallback sans fade si l'image refuse de charger
      ctx.main.removeAttribute('srcset');
      ctx.main.removeAttribute('sizes');
      ctx.main.setAttribute('src', src);
    };
    preloader.src = src;
  }

  function activeIdx(ctx) {
    return parseInt(ctx.root.dataset.cftActiveIdx || '0', 10) || 0;
  }

  function initGalleryDelegation() {
    // Click: thumbnails + prev/next arrows.
    document.addEventListener('click', (e) => {
      const thumb = e.target.closest('.cft-pp__thumb');
      if (thumb) {
        const ctx = galleryFor(thumb);
        if (ctx) showThumb(ctx, ctx.thumbs.indexOf(thumb));
        return;
      }
      const prev = e.target.closest('[data-cft-prev]');
      if (prev) {
        const ctx = galleryFor(prev);
        if (ctx) showThumb(ctx, activeIdx(ctx) - 1);
        return;
      }
      const next = e.target.closest('[data-cft-next]');
      if (next) {
        const ctx = galleryFor(next);
        if (ctx) showThumb(ctx, activeIdx(ctx) + 1);
      }
    });

    // Touch swipe on the main image wrap.
    let startX = 0;
    let startY = 0;
    let tracking = false;
    document.addEventListener(
      'touchstart',
      (e) => {
        const wrap = e.target.closest('.cft-pp__main-image-wrap');
        if (!wrap) { tracking = false; return; }
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      },
      { passive: true }
    );
    document.addEventListener(
      'touchend',
      (e) => {
        if (!tracking) return;
        tracking = false;
        const wrap = e.target.closest('.cft-pp__main-image-wrap');
        if (!wrap) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        // Horizontal swipe only, ignore vertical scrolls.
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        const ctx = galleryFor(wrap);
        if (!ctx) return;
        showThumb(ctx, activeIdx(ctx) + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
  }

  /* ---------- Boot ---------- */
  function boot() {
    initReveal();
    initCustomCursor();
    initGalleryDelegation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run reveal after Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
